import React, { useCallback, useContext, useMemo, useState } from 'react';
import styled from 'styled-components';
import { Container, flexColumn, SmallBodySemiBold, Table } from '@ubeya/shared-web/components';
import ConfigContext from '@ubeya/shared-web/contexts/ConfigContext';
import editable from '@ubeya/shared-web/style/editable';
import useAccount from '@ubeya/shared/hooks/account/useAccount';
import useAccountConfig from '@ubeya/shared/hooks/account/useAccountConfig';
import usePayrollFields from '@ubeya/shared/hooks/account/usePayrollFields';
import useSuppliers from '@ubeya/shared/hooks/account/useSuppliers';
import usePositions from '@ubeya/shared/hooks/account/usePositions';
import { SORT_BY_DATE_AND_TIME, SORT_BY_NAME, SORT_BY_NUMBER, SORT_BY_TOTAL } from '@ubeya/shared/utils/sorting';
import { EmployeeCard } from 'shared/components';
import { filterPayrollFieldsByDate } from 'shared/utils/payroll';
import { PAYROLL_CUSTOM_FIELD_TYPE } from 'shared/constants/payroll';
import {
  ClientTimesheetResponseStatus,
  EditableWageRenderer,
  FieldRenderer,
  TotalSalaryRenderer,
  TotalSalaryWithVatRenderer
} from 'payroll/components/CellRenderers';
import {
  BreaksRenderer,
  InTimeRenderer,
  NameRenderer,
  OutTimeRenderer,
  PositionRenderer,
  TotalTimeRenderer
} from 'timeSheet/components/CellRenderers';
import DifferenceRenderer from 'timeSheet/components/CellRenderers/DifferenceRenderer';
import { DISABLE_FILL_HOURS_BY_JOB_TYPE } from 'timeSheet/constants';
import ReadOnlyApproveRenderer from 'timeSheet/components/CellRenderers/ReadOnlyApproveRenderer';

const Wrapper = styled(Container)`
  ${flexColumn};
  flex: 1;
`;

const StyledTable = styled(Table)`
  .row {
    .avatar {
      display: none;
    }

    ${editable};

    &:hover {
      background-color: ${({ theme }) => theme.colors.white};
    }

    &:hover .underline {
      text-decoration: underline;
    }
  }

  .sub-row.error {
    background-color: ${({ theme }) => theme.colors.redExtraLight};
  }
`;

const MAX_ROWS_WITHOUT_VIRTUALIZE = 500;

const InvoiceTableReadOnly = ({ data, isClient = false, storageName, className, timeRange }) => {
  const { t } = useContext(ConfigContext);

  const [sort, setSort] = useState({ sortBy: 'name', sortDirection: 'asc' });
  const [selectedEmployee, setSelectedEmployee] = useState();
  const { accountId } = useAccount();
  const { timeFormat } = useContext(ConfigContext);

  const { fields: allFields } = usePayrollFields();
  const payrollFields = filterPayrollFieldsByDate(allFields, timeRange.start);

  const { account } = useAccountConfig();
  const { mappedPositions } = usePositions();
  const { mappedSupplierPositions } = useSuppliers();

  const isFlatWage = account?.payrollConfig?.flatIsActive;
  const disableWageEditionInProject = account?.payrollConfig?.disableWageEditionInProject;

  const readOnlyMode = true;

  const captureRowClick = useCallback(
    ({ rowData: { subRows, jobTypeId } }) => !!subRows || DISABLE_FILL_HOURS_BY_JOB_TYPE[jobTypeId],
    []
  );

  const fields = useMemo(() => (payrollFields || []).filter((payrollField) => !!payrollField.isClient), [
    payrollFields
  ]);

  const columns = useMemo(
    () => [
      {
        Header: '',
        dataKey: 'approved',
        Cell: ReadOnlyApproveRenderer,
        frozen: true,
        maxWidth: 60,
        minWidth: 60,
        sortable: false,
        captureRowClick: false,
        resizable: false
      },
      {
        Header: t('name'),
        dataKey: 'name',
        Cell: ({ rowData }) =>
          rowData.summarized ? (
            rowData?.shift ? (
              <SmallBodySemiBold>{`${rowData?.shift} ${t('total')}`}</SmallBodySemiBold>
            ) : (
              <></>
            )
          ) : (
            <NameRenderer
              rowData={rowData}
              columnData={{ accountId, onClickEmployee: setSelectedEmployee, showCaptain: true, showRating: true }}
            />
          ),

        flexGrow: 1,
        minWidth: 100,
        frozen: true,
        sortFunc: SORT_BY_NAME
      },

      {
        Header: t('position'),
        dataKey: 'date',
        Cell: PositionRenderer,
        columnData: { mappedPositions: isClient ? mappedSupplierPositions : mappedPositions },
        sortable: false,
        width: 150,
        category: t('basic')
      },

      {
        Header: t('in'),
        dataKey: 'in',
        Cell: InTimeRenderer,
        columnData: {
          timeFormat,
          showTimesheetOriginalHours: true,
          updateStartTime: null,
          updateEndTime: null,
          readOnly: readOnlyMode,
          onOutsideClickSave: true
        },
        minwidth: 100,
        maxWidth: 115,
        captureRowClick,
        sortFunc: SORT_BY_DATE_AND_TIME('actualIn'),
        category: t('basic')
      },
      {
        Header: t('out'),
        dataKey: 'out',
        Cell: OutTimeRenderer,
        columnData: {
          timeFormat,
          showTimesheetOriginalHours: true,
          updateEndTime: null,
          readOnly: readOnlyMode,
          onOutsideClickSave: true
        },
        minwidth: 100,
        maxWidth: 115,
        captureRowClick,
        sortFunc: SORT_BY_DATE_AND_TIME('actualOut'),
        category: t('basic')
      },
      {
        Header: t('totalTime'),
        dataKey: 'total',
        Cell: TotalTimeRenderer,
        columnData: {
          updateStartTime: null,
          updateEndTime: null,
          readOnly: readOnlyMode
        },
        sortFunc: SORT_BY_TOTAL,
        maxWidth: 90,
        category: t('basic')
      },
      {
        Header: t('total'),
        dataKey: 'totalSalary',
        Cell: TotalSalaryRenderer,
        maxWidth: 100,
        sortFunc: SORT_BY_NUMBER,
        category: t('payroll')
      },
      {
        Header: t('regular'),
        dataKey: 'payroll.aggregatedHours.regular',
        maxWidth: 100,
        sortFunc: SORT_BY_NUMBER,
        category: t('payroll')
      },
      {
        Header: t('overtime'),
        dataKey: 'payroll.aggregatedHours.overtime',
        maxWidth: 100,
        sortFunc: SORT_BY_NUMBER,
        category: t('payroll')
      },
      {
        Header: t('overtime2'),
        dataKey: 'payroll.aggregatedHours.overtime2',
        maxWidth: 100,
        sortFunc: SORT_BY_NUMBER,
        category: t('payroll')
      },
      {
        Header: t('holidayRegular'),
        dataKey: 'payroll.aggregatedHours.holidayRegular',
        maxWidth: 100,
        sortFunc: SORT_BY_NUMBER,
        category: t('payroll')
      },
      {
        Header: t('holidayOvertime'),
        dataKey: 'payroll.aggregatedHours.holidayOvertime',
        maxWidth: 100,
        sortFunc: SORT_BY_NUMBER,
        category: t('payroll')
      },
      {
        Header: t('holidayOvertime2'),
        dataKey: 'payroll.aggregatedHours.holidayOvertime2',
        maxWidth: 100,
        sortFunc: SORT_BY_NUMBER,
        category: t('payroll')
      },
      {
        Header: t('totalWithVAT'),
        dataKey: 'totalVATSalary',
        // columnData: { vat: 1,mappedBranches?.[branchId]?.payrollConfig?.vat },
        columnData: { vat: 1 },
        Cell: TotalSalaryWithVatRenderer,
        maxWidth: 100,
        sortFunc: SORT_BY_NUMBER,
        category: t('payroll')
      },

      {
        Header: t('difference'),
        dataKey: 'difference',
        Cell: DifferenceRenderer,
        sortFunc: SORT_BY_NUMBER('difference'),
        visible: false,
        maxWidth: 100,
        category: t('basic')
      },
      {
        Header: t('breaks'),
        dataKey: 'breaks',
        maxWidth: 100,
        Cell: BreaksRenderer,
        columnData: { updateBreak: null, deleteBreak: null, readOnly: readOnlyMode },
        sortable: false,
        category: t('basic')
      },

      //   {
      //     Header: t('hourlyRate'),
      //     dataKey: 'hourlyRate',
      //     Cell: EditableWageRenderer,
      //     columnData: {
      //       refetchPayroll,
      //       wageKey: 'hourlyWage',
      //       withPrefix: true,
      //       isEditable: readOnlyMode ? false : !disableWageEditionInProject
      //     },
      //     maxWidth: 100,
      //     captureRowClick,
      //     category: t('payroll'),
      //     sortable: false
      //   },
      isFlatWage && {
        Header: t('globalWage'),
        dataKey: 'flatWage',
        Cell: EditableWageRenderer,
        columnData: {
          refetchPayroll: null,
          parent: 'clientWage',
          wageKey: 'flatWage',
          withPrefix: true,
          isEditable: readOnlyMode ? false : !disableWageEditionInProject
        },
        maxWidth: 100,
        captureRowClick,
        category: t('payroll'),
        sortable: false
      },
      isFlatWage && {
        Header: t('flatBillRateHoursLimit'),
        dataKey: 'flatHours',
        Cell: EditableWageRenderer,
        columnData: {
          refetchPayroll: null,
          parent: 'clientWage',
          wageKey: 'flatHours',
          isEditable: readOnlyMode ? false : !disableWageEditionInProject
        },
        maxWidth: 100,
        captureRowClick,
        category: t('payroll'),
        sortable: false
      },
      {
        Header: t('clientWage'),
        dataKey: 'clientWage',
        Cell: EditableWageRenderer,
        columnData: {
          refetchPayroll: null,
          updateClientRate: null,
          clientWageCase: true,
          parent: 'clientWage',
          wageKey: 'hourlyWage',
          withPrefix: true,
          isEditable: readOnlyMode ? false : !disableWageEditionInProject
        },
        maxWidth: 100,
        captureRowClick,
        category: t('payroll'),
        sortable: false
      },
      {
        Header: t('clientStatus'),
        dataKey: 'clientApproved',
        Cell: ClientTimesheetResponseStatus,
        maxWidth: 120,
        captureRowClick,
        category: t('payroll'),
        sortable: false
      },
      ...fields.map(({ slug, id, isGeneric }) => ({
        Header: isGeneric ? t(slug) : slug,
        dataKey: `fields.${id}`,
        Cell: FieldRenderer,
        columnData: { fieldId: id, refetchPayroll: null, readOnly: readOnlyMode, isClient: true },
        captureRowClick,
        maxWidth: 100,
        sortable: false,
        category: t('payroll')
      })),
      ...fields
        .filter(({ type }) => type === PAYROLL_CUSTOM_FIELD_TYPE.HOURS_RELATED)
        .map(({ slug, id, isGeneric }) => ({
          Header: isGeneric ? t(slug) : `${slug} ${t('total')}`,
          dataKey: `fieldsTotal.${id}`,
          Cell: FieldRenderer,
          columnData: { fieldId: id, refetchPayroll: null, showTotal: true, readOnly: true, isClient: true },
          captureRowClick,
          maxWidth: 100,
          sortable: false,
          category: t('payroll')
        })),
      {
        Header: t('reviewOnEmployee'),
        dataKey: 'review',
        formatter: (value) => value,
        minWidth: 160,
        sortable: false,
        category: t('basic')
      },
      {
        Header: t('invoiceNumber'),
        dataKey: 'invoiceNumber',
        formatter: (value) => value,
        minWidth: 160,
        sortable: false,
        category: t('basic')
      }
    ],
    [
      t,
      isClient,
      mappedSupplierPositions,
      mappedPositions,
      timeFormat,
      readOnlyMode,
      captureRowClick,
      isFlatWage,
      disableWageEditionInProject,
      fields,
      accountId
    ]
  );

  const getRowStyle = useCallback(
    ({ summarized, actualIn, actualOut }) => (summarized ? 'summarized' : actualIn && !actualOut ? 'error' : ''),
    []
  );

  const isVirtualized = useCallback((total) => total > MAX_ROWS_WITHOUT_VIRTUALIZE, []);

  return (
    <Wrapper className={className}>
      <StyledTable
        columns={columns}
        data={data}
        rowStyle={getRowStyle}
        isVirtualized={isVirtualized}
        sortBy={sort.sortBy}
        sortDirection={sort.sortDirection}
        headerRowHeight={50}
        autoHeight
        useCalculationBySubRows
        setSort={setSort}
        sortable
        resizable
        showColumnsMenu
        columnsMenuTitle={t('configureInvoiceTable')}
        storageName={storageName}
        isStickyDrawer={false}
        allowFrozenUntilWidth={600}
      />

      {selectedEmployee && (
        <EmployeeCard defaultData={selectedEmployee} onClose={() => setSelectedEmployee()} refetchPayroll />
      )}
    </Wrapper>
  );
};

export default InvoiceTableReadOnly;
